import React, { createContext, useReducer, useEffect, useContext } from 'react';

const THEME_STORAGE_KEY = 'quantimet_dashboard_theme';
const THEME_DARK = 'dark';
const THEME_LIGHT = 'light';

const getInitialTheme = () => {
	const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
	if (savedTheme && (savedTheme === THEME_DARK || savedTheme === THEME_LIGHT)) {
		return savedTheme;
	}
	// Check system preference
	if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
		return THEME_LIGHT;
	}
	return THEME_DARK;
};

const initialState = {
	theme: getInitialTheme(),
};

const actions = {
	SET_THEME: 'SET_THEME',
	TOGGLE_THEME: 'TOGGLE_THEME',
};

const reducer = (state, action) => {
	switch (action.type) {
		case actions.SET_THEME:
			return { ...state, theme: action.theme };
		case actions.TOGGLE_THEME:
			return { ...state, theme: state.theme === THEME_DARK ? THEME_LIGHT : THEME_DARK };
		default:
			return state;
	}
};

export const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
	const [state, dispatch] = useReducer(reducer, initialState);

	// Persist theme to localStorage whenever it changes
	useEffect(() => {
		localStorage.setItem(THEME_STORAGE_KEY, state.theme);
	}, [state.theme]);

	const value = {
		theme: state.theme,
		isDark: state.theme === THEME_DARK,
		isLight: state.theme === THEME_LIGHT,
		setTheme: (theme) => {
			dispatch({ type: actions.SET_THEME, theme });
		},
		toggleTheme: () => {
			dispatch({ type: actions.TOGGLE_THEME });
		},
	};

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Custom hook for consuming theme context
export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

export { THEME_DARK, THEME_LIGHT };
export default ThemeProvider;
